import { useCallback, useEffect, useState } from 'react';
import { Badge, ProductCard, Slider, useIsTabletScreen } from '@carbonify/ui';
import { formatCurrency } from '@carbonify/ui/utils/format';
import clsx from 'clsx';
import CtaButton from 'components/CtaButton';
import InlineRichText from 'components/RichContent/InlineRichText';
import useLocalStorage from 'hooks/useLocalStorage';
import { ProductOptionsConfig, useProductOptions } from 'services/productOptions';

import UbaYearSelector from './UbaYearSelector';
import VehicleClassSelector from './VehicleClassSelector';

import styles from './ProductsModule.module.css';

interface ProductsModuleRecord {
  id: string;
  anchor?: string;
  __typename: string;

  /**
   * Markdown
   */
  title?: string;

  /**
   * Markdown
   */
  subtitle?: string;

  /**
   * Markdown
   */
  cta?: string;

  config?: ProductOptionsConfig;
}

interface ProductSelectionStorage {
  ubaYears: string;
  productOption: string;
  vehicleClass: string;
}

const ProductSelectionKey = 'LANDING_PAGE_PRODUCT_SELECTION';

const mapVehicleClassToLabel: Record<string, string> = {
  M1: 'pro PKW',
  M3: 'pro M3',
  N1: 'pro N1',
  N2: 'pro N2',
  N3: 'pro N3',
  Andere: 'pro E-Motorrad',
};

export default function ProductsModule({ title, subtitle, cta, anchor, config }: ProductsModuleRecord) {
  const [vehicleClass, setVehicleClass] = useState<string>();
  const [ubaYears, setUbaYears] = useState<string>();
  const {
    availableYears = [],
    productOptions = [],
    isLoading,
    availableVehicleClasses = [],
    defaultVehicleClass = 'M1',
  } = useProductOptions(vehicleClass, ubaYears, config);
  const isTablet = useIsTabletScreen();

  const [, storeProductSelection] = useLocalStorage<ProductSelectionStorage>(
    ProductSelectionKey,
    {} as ProductSelectionStorage,
  );

  useEffect(() => {
    if (isLoading) return;

    if (!vehicleClass || (!availableVehicleClasses.includes(vehicleClass) && vehicleClass !== defaultVehicleClass))
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVehicleClass(defaultVehicleClass);

    if (!ubaYears && availableYears.length) setUbaYears(availableYears[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, availableVehicleClasses, availableYears, defaultVehicleClass]);

  const handleClickProductOption = useCallback(
    (productOption: string) => {
      return () => {
        storeProductSelection({
          ubaYears: ubaYears || new Date().getFullYear().toString(),
          vehicleClass: vehicleClass || 'M1',
          productOption,
        });
      };
    },
    [ubaYears, vehicleClass, storeProductSelection],
  );

  return (
    <section
      data-testid={`products-module`}
      id={anchor}
      className={clsx(styles.module)}
      aria-busy={isLoading}
    >
      <div className={styles.bg}>
        <div className={styles.wrapper}>
          <div className={styles.titles}>
            {title && (
              <InlineRichText
                as="h2"
                className={styles.title}
              >
                {title}
              </InlineRichText>
            )}
            {subtitle && (
              <InlineRichText
                as="p"
                className={styles.subtitle}
              >
                {subtitle}
              </InlineRichText>
            )}
          </div>
        </div>
      </div>
      <div className={styles.wrapper}>
        {availableYears.length > 1 ? (
          <UbaYearSelector
            options={availableYears}
            value={ubaYears}
            onChange={setUbaYears}
          />
        ) : null}
        {isLoading ? (
          <Slider
            visibleItems={isTablet ? 1 : 2}
            hideButtonsWhenNotUsed
            items={[
              <ProductCard
                className={styles.card}
                loading
                key="loading-0"
              />,
              <ProductCard
                className={styles.card}
                loading
                key="loading-1"
              />,
            ]}
          />
        ) : (
          <Slider
            visibleItems={isTablet ? 1 : 2}
            hideButtonsWhenNotUsed
            gapOffset={20}
            items={productOptions.map(({ key, priceAsNode, subtext, name, description, coupon }) => (
              <ProductCard
                className={styles.card}
                titleSuffix={
                  coupon && (
                    <Badge>
                      {coupon.fixedAmount ? <b className="mr-[0.4ch]">+ {formatCurrency(coupon.fixedAmount)}</b> : null}
                      {coupon?.productTitle || coupon.title || coupon.couponCode}
                    </Badge>
                  )
                }
                coupon={coupon?.couponCode}
                couponPrice={coupon?.fixedAmount}
                // couponLabel={coupon ? `+ ${formatCurrency(coupon.fixedAmount)} ${coupon.title}` : undefined}
                key={key}
                price={priceAsNode}
                priceLabel={(vehicleClass && mapVehicleClassToLabel[vehicleClass]) || 'pro Kfz'}
                priceNote={subtext}
                title={name}
                description={description}
                cta={
                  cta && (
                    <CtaButton
                      value={cta}
                      interpolations={{ key, name, vehicleClass, ubaYears }}
                      borderRadius="2xl"
                      size="xl"
                      onClick={handleClickProductOption(key)}
                    />
                  )
                }
              />
            ))}
          />
        )}
        {availableVehicleClasses.length > 1 && (
          <VehicleClassSelector
            options={availableVehicleClasses}
            value={vehicleClass}
            onChange={setVehicleClass}
            className={styles.vehicleClass}
          />
        )}
      </div>
    </section>
  );
}
